import React, { type FC, use, useCallback, useMemo, useState } from "react";
import { Button, Dropdown, type Key, Label, SearchField, Separator, Spinner, Tooltip } from "@heroui/react";

import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import { AgentRunContext } from "@app/pages/home/agent/AgentRunProvider";
import { AppRoutes } from "@domain/routing/routeConstants";
import { CompanyContext } from "@domain/context/CompanyContext";
import { UserCompaniesContext, UserContext, UserControlContext } from "@domain/context/UserProvider";

import { ChevronDown, CircleCheck, Gear, LayoutCellsLarge, TriangleExclamation } from "@gravity-ui/icons";

const SEARCH_KEY = "search";

const stopPropagation = (e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation();

export const CompanyDropdown: FC = () => {
    const { formatMessage } = useIntl();
    const user = use(UserContext);
    const { companyGQL, isLoaded } = use(CompanyContext);
    const companies = use(UserCompaniesContext);
    const { onSelectCompany } = use(UserControlContext);
    const { hasRun: agentActive } = use(AgentRunContext);
    const [term, setTerm] = useState("");

    const location = useLocation();
    const navigate = useNavigate();

    const handleAction = useCallback(
        (id: Key) => {
            if (id === AppRoutes.manageCompanies) {
                return navigate(AppRoutes.manageCompanies);
            }
            if (id === AppRoutes.adminVatNumbers) {
                return navigate(AppRoutes.adminVatNumbers);
            }
            if (id === AppRoutes.adminMetroAccountingCategories) {
                return navigate(AppRoutes.adminMetroAccountingCategories);
            }
            if (!id || id === SEARCH_KEY) {
                return;
            }
            navigate("/");
            onSelectCompany(id.toString());
        },
        [navigate, onSelectCompany],
    );

    const sortedCompanies = useMemo(() => {
        return [...companies].filter((c) => !c.isLocked).toSorted((a, b) => a.name.localeCompare(b.name));
    }, [companies]);

    const filteredCompanies = useMemo(() => {
        return sortedCompanies.filter((c) => !term.length || c.name.toLowerCase().includes(term.toLowerCase()));
    }, [sortedCompanies, term]);

    const onOpenCompanySettings = useCallback(() => {
        if (!companyGQL) {
            return;
        }
        navigate(AppRoutes.manageCompanies + "?search=" + encodeURI(companyGQL.name.toLowerCase()));
    }, [navigate, companyGQL]);

    const onOpenCompaniesOverview = useCallback(() => navigate(AppRoutes.manageCompanies), [navigate]);

    const onPopoverOpenChange = useCallback((open: boolean) => {
        if (!open) {
            setTerm("");
        }
    }, []);

    const onSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (e.key === "Escape") {
            setTerm("");
        }
    }, []);

    const selectedKeys = useMemo(
        () => [
            location.pathname === AppRoutes.adminMetroArticles
                ? AppRoutes.adminMetroAccountingCategories
                : location.pathname,
        ],
        [location.pathname],
    );

    if (!user.isUserDataLoaded) {
        return null;
    }
    // No "+ Company" affordance here: companies are provisioned by staff and the
    // customer is assigned as owner. A signed-in user without a company sees the
    // waiting state rendered by AuthWrapper.AuthenticatedRoute instead.
    if (!companyGQL) {
        // Staff, however, must keep a route to the companies overview - it is the only
        // place a company can still be created, and the dropdown below (which links to
        // it) only renders once a company is selected.
        if (user.isAdmin) {
            return (
                <Button
                    variant="outline"
                    className="bg-app-surface mr-2"
                    onPress={onOpenCompaniesOverview}
                    data-testid="header-companies-overview"
                >
                    <Gear className="size-4" /> <FormattedMessage id="app.titles.company.pl" />
                </Button>
            );
        }
        return null;
    }

    return (
        <div className="flex items-center gap-2">
            <Button
                variant="secondary"
                size="sm"
                isIconOnly
                data-testid="header-company-settings"
                onPress={onOpenCompanySettings}
            >
                <Gear className="size-4" />
            </Button>

            {agentActive ? (
                // A Binale Agent run belongs to one company; switching starts only after it ended.
                <Tooltip delay={0} closeDelay={0}>
                    <Tooltip.Trigger data-testid="header-company-locked">
                        <Button
                            variant="outline"
                            className="bg-app-surface mr-2"
                            isDisabled
                            data-testid="header-dropdown-company-list"
                        >
                            {companyGQL.name} <ChevronDown className="size-4" />
                        </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Content placement="bottom">
                        <FormattedMessage id="app.agent.companyLocked" />
                    </Tooltip.Content>
                </Tooltip>
            ) : (
                <Dropdown>
                    <Dropdown.Trigger>
                        <Button
                            variant="outline"
                            className="bg-app-surface mr-2"
                            isPending={!isLoaded}
                            data-testid="header-dropdown-company-list"
                        >
                            {({ isPending }) => (
                                <>
                                    {isPending ? <Spinner color="current" size="sm" /> : null}
                                    {companyGQL?.name || "..."} <ChevronDown className="size-4" />
                                </>
                            )}
                        </Button>
                    </Dropdown.Trigger>
                    <Dropdown.Popover onOpenChange={onPopoverOpenChange}>
                        <div
                            className="px-1 pb-1 pt-1 bg-linear-to-b from-default/90 to-default/0"
                            data-testid="header-dropdown-company-list-search"
                            onClick={stopPropagation}
                            onKeyDown={onSearchKeyDown}
                        >
                            <SearchField
                                autoFocus
                                className="m-2"
                                aria-label={formatMessage({ id: "app.fields.search" })}
                                value={term}
                                onChange={setTerm}
                            >
                                <SearchField.Group className="shadow-md border">
                                    <SearchField.SearchIcon />
                                    <SearchField.Input />
                                    <SearchField.ClearButton />
                                </SearchField.Group>
                            </SearchField>
                        </div>
                        {/* The menu would grab focus on open; leave it to the search field. */}
                        <Dropdown.Menu
                            autoFocus={false}
                            aria-label="Company list"
                            selectedKeys={selectedKeys}
                            onAction={handleAction}
                            className="max-h-[400px] overflow-y-auto"
                        >
                            <Dropdown.Section>
                                {filteredCompanies.map((c) => {
                                    const isActive = c.id === companyGQL?.id;
                                    return (
                                        <Dropdown.Item
                                            key={c.id}
                                            id={c.id}
                                            textValue={c.name}
                                            className={isActive ? "bg-accent/20" : undefined}
                                        >
                                            <span className="flex items-center justify-between w-full">
                                                <Label>{c.name}</Label>
                                                <CircleCheck
                                                    className={`size-4 ${isActive ? "opacity-100" : "opacity-0"}`}
                                                />
                                            </span>
                                        </Dropdown.Item>
                                    );
                                })}
                            </Dropdown.Section>
                            <Separator />
                            <Dropdown.Section>
                                <Dropdown.Item id={AppRoutes.manageCompanies} textValue="Einstellungen">
                                    <Gear className="size-4" />
                                    <Label>
                                        <FormattedMessage id="app.titles.settings.manage" />
                                    </Label>
                                </Dropdown.Item>
                            </Dropdown.Section>
                            {user.isAdmin ? (
                                <Dropdown.Section>
                                    <Dropdown.Item id={AppRoutes.adminVatNumbers} textValue="VAT Numbers">
                                        <TriangleExclamation className="size-4" />
                                        <Label>
                                            <FormattedMessage id="app.companies.vatNumbers" />
                                        </Label>
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        id={AppRoutes.adminMetroAccountingCategories}
                                        textValue={formatMessage({ id: "app.companies.metroAdministration" })}
                                    >
                                        <LayoutCellsLarge className="size-4" />
                                        <Label>
                                            <FormattedMessage id="app.companies.metroAdministration" />
                                        </Label>
                                    </Dropdown.Item>
                                </Dropdown.Section>
                            ) : null}
                        </Dropdown.Menu>
                    </Dropdown.Popover>
                </Dropdown>
            )}
        </div>
    );
};
